import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, MapPin, User, ArrowRight, Loader2 } from "lucide-react";
import { usePendingDeals, formatNaira } from "@/hooks/use-supabase-queries";
import { formatDistanceToNow } from "date-fns";

const transactionTypeConfig: Record<string, { label: string; className: string }> = {
  purchase: { label: "Purchase", className: "bg-primary/10 text-primary" },
  sale: { label: "Sale", className: "bg-accent/20 text-accent-foreground" },
  lease: { label: "Lease", className: "bg-secondary text-secondary-foreground" },
};

interface DealIntakesCardProps {
  onReviewIntake?: (intakeId: string) => void;
}

export function DealIntakesCard({ onReviewIntake }: DealIntakesCardProps) {
  const { data: pendingDeals, isLoading } = usePendingDeals();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const deals = pendingDeals || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Pending Instructions</CardTitle>
          <p className="text-sm text-muted-foreground">{deals.length} pending review</p>
        </div>
        {deals.length > 0 && (
          <Badge variant="secondary" className="bg-accent text-accent-foreground">Needs Attention</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {deals.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No pending instructions from agents right now.</p>
        ) : (
          deals.map((deal) => (
            <div key={deal.id} className="p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-foreground">{deal.property_title}</h4>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                    <MapPin className="h-3 w-3" />
                    {deal.property_address}
                  </div>
                </div>
                {deal.deal_type && (
                  <Badge className={transactionTypeConfig[deal.deal_type]?.className || ''}>
                    {transactionTypeConfig[deal.deal_type]?.label || deal.deal_type}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {deal.buyer_name} (Buyer)
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {deal.submitted_at ? formatDistanceToNow(new Date(deal.submitted_at), { addSuffix: true }) : 'Recently'}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-primary">{formatNaira(deal.estimated_value)}</span>
                <Button size="sm" variant="outline" onClick={() => onReviewIntake?.(deal.id)}>
                  Review <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
